<template>
  <component
    :is="tag"
    :to="to"
    :class="['row', riskClass, { 'is-in': revealed }]"
    :data-risk="risk"
    :style="{ transitionDelay: `${delay}ms` }"
    @click="$emit('open')"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";

const props = withDefaults(
  defineProps<{
    risk: "high" | "med" | "low";
    to?: RouteLocationRaw;
    /** Reveal delay in ms (cascading stagger). */
    delay?: number;
  }>(),
  { delay: 0 },
);

defineEmits<{ open: [] }>();

const tag = props.to ? RouterLink : "div";
const revealed = ref(false);

/** Legacy CSS hook used by tests + earlier consumer styles. */
const riskClass = computed(() => {
  if (props.risk === "high") return "red";
  if (props.risk === "med") return "amber";
  return "clean";
});

onMounted(() => {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) {
    revealed.value = true;
    return;
  }
  // The parent screen drives reveal timing via the data-delay token.
  // We honor the prop independently in case the row is mounted standalone.
  window.setTimeout(() => { revealed.value = true; }, props.delay);
});
</script>

<style scoped>
.row {
  display: grid;
  grid-template-columns: 64px 1fr 460px 100px;
  gap: 28px; align-items: center;
  padding: 24px 8px 24px 0;
  border-bottom: 1px solid var(--line);
  position: relative;
  cursor: pointer;
  text-decoration: none; color: inherit;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity var(--speed-reveal) ease,
              transform var(--speed-reveal) var(--ease-reveal),
              background var(--speed-standard) ease;
}
.row.is-in { opacity: 1; transform: none; }
.row:hover {
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--bg-elev) 60%, transparent) 20%,
    color-mix(in srgb, var(--bg-elev) 60%, transparent) 80%,
    transparent
  );
}
.row:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--brand) 22%, transparent);
}

.row::before {
  content: "";
  position: absolute; left: 0; top: 12px; bottom: 12px;
  width: 3px; border-radius: 3px;
  transform: scaleY(0); transform-origin: top;
  transition: transform 220ms var(--ease-reveal);
  transition-delay: inherit;
}
.row[data-risk="high"]::before { background: var(--red); }
.row[data-risk="med"]::before  { background: var(--amber); }
.row[data-risk="low"]::before  { background: var(--green); }
.row.is-in::before { transform: scaleY(1); }

@media (prefers-reduced-motion: reduce) {
  .row { opacity: 1 !important; transform: none !important; transition: background var(--speed-standard) ease; }
  .row::before { transform: scaleY(1) !important; transition: none !important; }
}

@media (max-width: 1100px) {
  .row { grid-template-columns: 56px 1fr; gap: 20px; }
  .row :deep(.metrics), .row :deep(.right) { grid-column: 1 / -1; }
}
</style>
